/* eslint-disable complexity */
import React, { useMemo } from 'react';

import { LocalizeCurrency } from '@change-corgi/core/react/i18n';
import { buttonResetStyles } from '@change-corgi/design-system-legacy/reset';
import { Text } from '@change-corgi/design-system-legacy/typography';

type Props = {
	amount: number;
	currency: string;
	isSelected: boolean;
	onClick: () => void;
	dataQaAttribute?: string;
	isSunriseEnabled: boolean;
	height?: `${number}px`;
	textSize?: 'small' | 'default' | 'large';
	fontWeight?: 'semibold' | 'bold' | 'normal';
	borderWidth?: string;
	borderWidthUnselected?: string;
	backgroundColor?: string;
	backgroundColorUnselected?: string;
	color?: string;
	colorUnselected?: string;
	borderColorUnselected?: string;
	hideOutlineWhenSelected?: boolean;
	borderColor?: string;
};

export const AmountButton: React.FC<Props> = ({
	amount,
	isSelected,
	onClick,
	currency,
	dataQaAttribute,
	height,
	isSunriseEnabled,
	textSize = 'small',
	fontWeight = 'semibold',
	borderWidth,
	borderWidthUnselected,
	backgroundColor,
	backgroundColorUnselected,
	color,
	colorUnselected,
	borderColorUnselected,
	hideOutlineWhenSelected = false,
	borderColor,
}) => {
	const colorStyles = useMemo(() => {
		if (isSelected)
			return {
				color: 'typography-primary',
				backgroundColor: 'rgba(21, 125, 185, 0.08)',
				borderColor: 'secondary-blue',
				...(isSunriseEnabled && {
					backgroundColor: 'sunrise.semantic.fill.accent.primary.weaker',
					borderColor: 'sunrise.semantic.fill.accent.primary.strong',
				}),
			};
		return {
			color: 'typography-primary',
			backgroundColor: 'primary-white',
			borderColor: 'neutral-grey100',
			...(isSunriseEnabled && {
				borderColor: 'sunrise.semantic.fill.weak',
			}),
		};
	}, [isSelected, isSunriseEnabled]);

	return (
		<button
			type="button"
			data-amount={amount}
			data-testid="payment-form-amount-button"
			data-qa={dataQaAttribute || 'contribution-amount-button'}
			onClick={onClick}
			aria-pressed={isSelected}
			aria-label={`${amount} ${currency}`}
			sx={{
				...buttonResetStyles,
				borderStyle: 'solid',
				borderWidth: isSelected ? '2px' : '1px',
				borderRadius: 'standard',
				width: '100%',
				height: height ?? '48px',
				fontWeight: 'bold',
				':focus-within': {
					outlineWidth: isSelected && hideOutlineWhenSelected ? '0px' : '1px',
					outlineColor: 'secondary-blue',
					outlineStyle: 'solid',
				},
				...colorStyles,
				...(borderWidth && { borderWidth: isSelected ? borderWidth : borderWidthUnselected }),
				...(backgroundColor && { backgroundColor: isSelected ? backgroundColor : backgroundColorUnselected }),
				...(borderColorUnselected && !isSelected && { borderColor: borderColorUnselected }),
				...(borderColor && isSelected && { borderColor }),
			}}
		>
			<Text size={textSize} fontWeight={fontWeight} color={isSelected ? color : colorUnselected}>
				<LocalizeCurrency value={amount} currency={currency} />
			</Text>
		</button>
	);
};
